import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ImageUp, Link2, Trash2 } from "lucide-react";
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
      BrandProfile,
      type BrandProfileFormData,
      type BrandProfileReviewSubmission,
} from "@/components/sidebar/brand-profile";
import { DashboardPageShell } from "@/components/patterns/headers";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { LoadingButton } from "@/components/ui/custom/loading-button";
import { fieldMessageVariants } from "@/components/ui/field-styles";
import { Input } from "@/components/ui/input";
import { normalizeLogoUrl } from "@/lib/logo-url";
import { cn } from "@/lib/utils";
import { useSupabaseBroadcast } from "@/hooks/use-supabase-broadcast";
import { useTRPC } from "@/lib/trpc";
import { useBrandStore } from "@/state/store/brand";
import { useSearchSpecificationStore } from "@/state/store/searchSpecification";

export const Route = createFileRoute("/_dashboard/$brand/brand")({
      component: RouteComponent,
});

const ACCEPTED_BRAND_LOGO_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
const BRAND_LOGO_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
type LogoSourceMode = "upload" | "url";

function isAutoGeneratedLogoUrl(url: string): boolean {
      try {
            return new URL(url).hostname === "api.companyenrich.com";
      } catch {
            return false;
      }
}

function RouteComponent() {
      const queryClient = useQueryClient();
      const trpc = useTRPC();
      const navigate = useNavigate();
      const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
      const [isUploadingLogo, setIsUploadingLogo] = useState(false);
      const [logoSourceMode, setLogoSourceMode] = useState<LogoSourceMode>("upload");
      const [logoUrlDraft, setLogoUrlDraft] = useState("");
      const logoFileInputRef = useRef<HTMLInputElement | null>(null);

      const selectedBrandId = useBrandStore((state) => state.selectedBrandId);
      const setSelectedBrandId = useBrandStore((state) => state.setSelectedBrandId);
      const clearSearchSpecification = useSearchSpecificationStore((state) => state.clear);

      const invalidateBrandProfileState = useCallback(async () => {
            if (!selectedBrandId) return;

            await Promise.all([
                  queryClient.invalidateQueries({
                        queryKey: trpc.brands.getBrand.queryKey({ id: selectedBrandId }),
                  }),
                  queryClient.invalidateQueries({
                        queryKey: trpc.brands.getBrandProfileGenerationState.queryKey({ brandId: selectedBrandId }),
                  }),
                  queryClient.invalidateQueries({
                        queryKey: trpc.brands.getOrganizationBrands.queryKey({}),
                  }),
            ]);
      }, [queryClient, selectedBrandId, trpc]);

      const { data: generationState } = useQuery(
            trpc.brands.getBrandProfileGenerationState.queryOptions(
                  { brandId: selectedBrandId! },
                  {
                        enabled: !!selectedBrandId,
                        refetchInterval: (query) =>
                              query.state.data?.activeGeneration ? 1500 : false,
                  },
            ),
      );
      const { data: brand } = useQuery(
            trpc.brands.getBrand.queryOptions(
                  { id: selectedBrandId! },
                  {
                        enabled: !!selectedBrandId,
                  },
            ),
      );

      useSupabaseBroadcast({
            channelName: selectedBrandId ? `brand_populated_successfully:${selectedBrandId}` : "",
            event: "brand_populated_successfully",
            callbackFn: () => {
                  void invalidateBrandProfileState();
            },
      });

      useEffect(() => {
            const logo = brand?.logo ?? "";
            setLogoUrlDraft(isAutoGeneratedLogoUrl(logo) ? "" : logo);
      }, [brand?.logo]);

      const deleteBrandMutation = useMutation(
            trpc.brands.deleteBrand.mutationOptions({
                  onSuccess: () => {
                        toast.success("Brand archived. You can restore it from Activity History.");
                        setIsDeleteDialogOpen(false);
                        setSelectedBrandId(null);
                        // Clear stale search specification ID to prevent errors when creating a new brand
                        clearSearchSpecification();
                        queryClient.invalidateQueries({
                              queryKey: trpc.brands.getOrganizationBrands.queryKey({}),
                        });
                        navigate({ to: "/" });
                  },
                  onError: (error) => {
                        toast.error(error.message || "Failed to archive brand");
                  },
            }),
      );

      const updateBrandMutation = useMutation(
            trpc.brands.updateBrand.mutationOptions({
                  onSuccess: () => {
                        void invalidateBrandProfileState();
                  },
            }),
      );

      const updateBrandLogoMutation = useMutation(
            trpc.brands.updateBrand.mutationOptions({
                  onSuccess: () => {
                        void invalidateBrandProfileState();
                  },
            }),
      );

      const createBrandLogoUploadMutation = useMutation(trpc.brands.createBrandLogoUpload.mutationOptions());

      const regenerateBrandProfileMutation = useMutation(
            trpc.brands.regenerateBrandProfile.mutationOptions({
                  onSuccess: () => {
                        toast.success("Generating a fresh brand profile preview.");
                        void invalidateBrandProfileState();
                  },
                  onError: (error) => {
                        toast.error(error.message || "Failed to regenerate the brand profile");
                  },
            }),
      );

      const applyPendingBrandProfileMutation = useMutation(
            trpc.brands.applyPendingBrandProfile.mutationOptions({
                  onError: (error) => {
                        toast.error(error.message || "Failed to apply the regenerated profile");
                  },
            }),
      );

      const discardPendingBrandProfileMutation = useMutation(
            trpc.brands.discardPendingBrandProfile.mutationOptions({
                  onSuccess: () => {
                        toast.success("Regenerated preview discarded.");
                        void invalidateBrandProfileState();
                  },
                  onError: (error) => {
                        toast.error(error.message || "Failed to discard the regenerated preview");
                  },
            }),
      );

      const handleSubmit = async (
            formData: BrandProfileFormData,
            reviewSubmission?: BrandProfileReviewSubmission,
      ) => {
            if (!selectedBrandId) {
                  toast.error("Brand context is still loading.");
                  return;
            }

            if (reviewSubmission?.hasPendingReview) {
                  await applyPendingBrandProfileMutation.mutateAsync({
                        brandId: selectedBrandId,
                        overwriteFields: reviewSubmission.overwriteFields,
                        discardFields: reviewSubmission.discardFields,
                  });
            }

            await updateBrandMutation.mutateAsync({
                  id: selectedBrandId,
                  name: formData.name,
                  website: formData.website,
                  industry: formData.industry,
                  description: formData.description,
                  products: formData.keyFeatures,
            });
      };

      const handleRegenerateBrandProfile = async () => {
            await regenerateBrandProfileMutation.mutateAsync({ id: selectedBrandId! });
      };

      const handleDiscardPendingBrandProfile = async () => {
            await discardPendingBrandProfileMutation.mutateAsync({ brandId: selectedBrandId! });
      };

      const handleOpenLogoPicker = () => {
            logoFileInputRef.current?.click();
      };

      const handleLogoFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            event.target.value = "";

            if (!file || !selectedBrandId) {
                  return;
            }

            if (!ACCEPTED_BRAND_LOGO_TYPES.includes(file.type as (typeof ACCEPTED_BRAND_LOGO_TYPES)[number])) {
                  toast.error("Upload a PNG, JPEG, or WebP logo.");
                  return;
            }

            if (file.size > BRAND_LOGO_MAX_FILE_SIZE_BYTES) {
                  toast.error("Logo files must be 5 MB or smaller.");
                  return;
            }

            setIsUploadingLogo(true);

            try {
                  const uploadTarget = await createBrandLogoUploadMutation.mutateAsync({
                        brandId: selectedBrandId,
                        fileName: file.name,
                        contentType: file.type as (typeof ACCEPTED_BRAND_LOGO_TYPES)[number],
                        contentLength: file.size,
                  });

                  const response = await fetch(uploadTarget.uploadUrl, {
                        method: uploadTarget.method,
                        headers: uploadTarget.headers,
                        body: file,
                  });

                  if (!response.ok) {
                        throw new Error(`Upload failed (status ${response.status})`);
                  }

                  await updateBrandLogoMutation.mutateAsync({
                        id: selectedBrandId,
                        logo: uploadTarget.assetUrl,
                  });

                  toast.success(brand?.logo ? "Brand logo updated." : "Brand logo uploaded.");
            } catch (error) {
                  console.error("Failed to upload brand logo", error);
                  toast.error("Failed to upload the brand logo");
            } finally {
                  setIsUploadingLogo(false);
            }
      };

      const handleRemoveLogo = async () => {
            if (!selectedBrandId || !brand?.logo) {
                  return;
            }

            try {
                  await updateBrandLogoMutation.mutateAsync({
                        id: selectedBrandId,
                        logo: null,
                  });
                  toast.success("Brand logo removed.");
            } catch (error) {
                  console.error("Failed to remove brand logo", error);
                  toast.error("Failed to remove the brand logo");
            }
      };

      const normalizedLogoUrl = normalizeLogoUrl(logoUrlDraft);
      const hasLogoUrlDraft = logoUrlDraft.trim().length > 0;
      const logoUrlError =
            hasLogoUrlDraft && !normalizedLogoUrl
                  ? "Enter a valid public http(s) image URL."
                  : null;

      const handleSaveLogoUrl = async () => {
            if (!selectedBrandId || !normalizedLogoUrl) {
                  toast.error("Enter a valid public logo URL.");
                  return;
            }

            try {
                  await updateBrandLogoMutation.mutateAsync({
                        id: selectedBrandId,
                        logo: normalizedLogoUrl,
                  });
                  toast.success("Brand logo URL saved.");
            } catch (error) {
                  console.error("Failed to save brand logo URL", error);
                  toast.error("Failed to save the brand logo URL");
            }
      };

      const logoActionPending =
            isUploadingLogo ||
            createBrandLogoUploadMutation.isPending ||
            updateBrandLogoMutation.isPending;
      const previewLogoUrl =
            logoSourceMode === "url" && hasLogoUrlDraft ? normalizedLogoUrl : brand?.logo;

      return (
            <>
                  <DashboardPageShell as="div" contentClassName="space-y-6">
                        {selectedBrandId ? (
                              <BrandProfile
                                    title="Brand Profile"
                                    subtitle="Manage the brand identity settings for the project"
                                    brandId={selectedBrandId}
                                    brandData={brand ?? undefined}
                                    onSubmit={handleSubmit}
                                    isSubmitting={
                                          updateBrandMutation.isPending || applyPendingBrandProfileMutation.isPending
                                    }
                                    topContent={
                                          <>
                                                <input
                                                      ref={logoFileInputRef}
                                                      type="file"
                                                      accept={ACCEPTED_BRAND_LOGO_TYPES.join(",")}
                                                      className="hidden"
                                                      onChange={handleLogoFileChange}
                                                />
                                                <div className={cn("flex gap-4", logoSourceMode === "url" ? "items-start" : "items-center")}>
                                                      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-brand-ink/[0.04]">
                                                            {previewLogoUrl ? (
                                                                  <img
                                                                        src={previewLogoUrl}
                                                                        alt={`${brand?.name ?? "Brand"} logo`}
                                                                        className="h-full w-full object-contain p-2"
                                                                  />
                                                            ) : (
                                                                  <div className="flex h-full w-full items-center justify-center text-brand-ink/20">
                                                                        <ImageUp className="h-5 w-5" />
                                                                  </div>
                                                            )}
                                                      </div>

                                                      {logoSourceMode === "upload" ? (
                                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                                                  <LoadingButton
                                                                        type="button"
                                                                        variant="brandOutline"
                                                                        onClick={handleOpenLogoPicker}
                                                                        loading={logoActionPending}
                                                                        disabled={logoActionPending}
                                                                        loadingText={brand?.logo ? "Replacing\u2026" : "Uploading\u2026"}
                                                                  >
                                                                        <ImageUp className="h-3.5 w-3.5" />
                                                                        {brand?.logo ? "Replace logo" : "Upload logo"}
                                                                  </LoadingButton>
                                                                  <span className="h-4 w-px bg-brand-ink/10" aria-hidden="true" />
                                                                  <button
                                                                        type="button"
                                                                        onClick={() => setLogoSourceMode("url")}
                                                                        disabled={logoActionPending}
                                                                        className="flex items-center gap-1.5 text-xs font-medium text-brand-ink/35 transition-colors hover:text-brand-ink/55 disabled:opacity-50"
                                                                  >
                                                                        <Link2 className="h-3 w-3" />
                                                                        Paste URL
                                                                  </button>
                                                                  {brand?.logo && (
                                                                        <>
                                                                              <span className="h-4 w-px bg-brand-ink/10" aria-hidden="true" />
                                                                              <button
                                                                                    type="button"
                                                                                    onClick={() => void handleRemoveLogo()}
                                                                                    disabled={logoActionPending}
                                                                                    className="text-xs text-brand-ink/30 transition-colors hover:text-brand-ink/50 disabled:opacity-50"
                                                                              >
                                                                                    Remove
                                                                              </button>
                                                                        </>
                                                                  )}
                                                            </div>
                                                      ) : (
                                                            <div className="flex min-w-0 flex-1 flex-col gap-2">
                                                                  <div className="flex items-center gap-2">
                                                                        <div className="min-w-0 flex-1">
                                                                              <Input
                                                                                    id="brand-logo-url"
                                                                                    type="url"
                                                                                    variant="brand"
                                                                                    value={logoUrlDraft}
                                                                                    onChange={(event) => setLogoUrlDraft(event.target.value)}
                                                                                    placeholder="https://example.com/logo.png"
                                                                                    aria-invalid={Boolean(logoUrlError)}
                                                                                    disabled={logoActionPending}
                                                                              />
                                                                              {logoUrlError && (
                                                                                    <p className={fieldMessageVariants({ tone: "error" })}>
                                                                                          {logoUrlError}
                                                                                    </p>
                                                                              )}
                                                                        </div>
                                                                        <LoadingButton
                                                                              type="button"
                                                                              variant="brand"
                                                                              onClick={() => void handleSaveLogoUrl()}
                                                                              loading={updateBrandLogoMutation.isPending}
                                                                              disabled={logoActionPending || !normalizedLogoUrl}
                                                                              loadingText="Saving\u2026"
                                                                        >
                                                                              Save
                                                                        </LoadingButton>
                                                                  </div>
                                                                  <div className="flex items-center gap-x-3">
                                                                        <button
                                                                              type="button"
                                                                              onClick={() => setLogoSourceMode("upload")}
                                                                              disabled={logoActionPending}
                                                                              className="flex items-center gap-1.5 text-xs font-medium text-brand-ink/35 transition-colors hover:text-brand-ink/55 disabled:opacity-50"
                                                                        >
                                                                              <ImageUp className="h-3 w-3" />
                                                                              Upload instead
                                                                        </button>
                                                                        {brand?.logo && (
                                                                              <>
                                                                                    <span className="h-3.5 w-px bg-brand-ink/10" aria-hidden="true" />
                                                                                    <button
                                                                                          type="button"
                                                                                          onClick={() => void handleRemoveLogo()}
                                                                                          disabled={logoActionPending}
                                                                                          className="text-xs text-brand-ink/30 transition-colors hover:text-brand-ink/50 disabled:opacity-50"
                                                                                    >
                                                                                          Remove
                                                                                    </button>
                                                                              </>
                                                                        )}
                                                                  </div>
                                                            </div>
                                                      )}
                                                </div>
                                          </>
                                    }
                                    generationState={generationState}
                                    onDismissPendingBrandProfile={handleDiscardPendingBrandProfile}
                                    isDismissingPendingBrandProfile={discardPendingBrandProfileMutation.isPending}
                                    onRegenerateBrandProfile={handleRegenerateBrandProfile}
                                    isRegeneratingBrandProfile={regenerateBrandProfileMutation.isPending}
                                    regenerateBrandProfileDisabled={Boolean(generationState?.activeGeneration)}
                              />
                        ) : (
                              <div className="mx-auto w-full max-w-4xl rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
                                    Loading brand context...
                              </div>
                        )}
                        <div className="mx-auto w-full max-w-4xl pt-10">
                              <div className="flex items-center justify-between rounded-xl border border-brand-ink/[0.06] px-5 py-4">
                                    <div className="space-y-1">
                                          <p className="text-[0.8125rem] font-medium text-brand-ink/65">Danger Zone</p>
                                          <p className="text-xs text-brand-ink/30">
                                                Archive this brand and hide it from active workflows. Historical search evidence
                                                stays recorded, and you can restore the archive from Activity History.
                                          </p>
                                    </div>
                                    <Button
                                          variant="destructive"
                                          size="sm"
                                          onClick={() => setIsDeleteDialogOpen(true)}
                                          className="gap-1.5"
                                    >
                                          <Trash2 className="h-3.5 w-3.5" />
                                          Archive Brand
                                    </Button>
                              </div>
                        </div>
                  </DashboardPageShell>
                  <ConfirmationDialog
                        open={isDeleteDialogOpen}
                        onOpenChange={setIsDeleteDialogOpen}
                        title="Archive Brand"
                        description="This brand and its reversible child resources will be archived from active use. Historical search evidence stays recorded, and you can roll back the archive from Activity History."
                        confirmText="Archive Brand"
                        onConfirm={() => {
                              if (!selectedBrandId) {
                                    toast.error("Brand context is still loading.");
                                    return;
                              }

                              deleteBrandMutation.mutate({ id: selectedBrandId });
                        }}
                        isPending={deleteBrandMutation.isPending}
                        variant="destructive"
                  />
            </>
      );
}
